import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import { ErrorAlert } from '../components/ErrorAlert';
import { useNavigate } from 'react-router-dom';
import {
  useBootstrapStatus,
  useCompleteBootstrap,
  useConfirmTotp,
  useLogin,
  useSetupTotp,
  useVerifyTotp,
} from '../api/queries';
import { setSessionToken } from '../auth/useAccessToken';
import { AuthShell } from '../components/auth/AuthShell';
import { OtpInput } from '../components/auth/OtpInput';
import { PasswordField } from '../components/auth/PasswordField';

const STEPS = ['Account', 'Two-factor', 'Done'];

/**
 * First-user wizard. Three steps:
 *   1. Collect username + email + display name + password.
 *   2. Bootstrap creates the user → log in → display TOTP QR.
 *   3. Enter the 6-digit code → confirm enrolment → re-login + verify
 *      to mint the full session JWT → redirect to /home.
 */
export default function BootstrapPage() {
  const navigate = useNavigate();
  const status = useBootstrapStatus();

  const bootstrap = useCompleteBootstrap();
  const login     = useLogin();
  const setup     = useSetupTotp();
  const confirm   = useConfirmTotp();
  const verify    = useVerifyTotp();

  const [step, setStep] = useState(0);
  const [username, setUsername]       = useState('');
  const [email, setEmail]             = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword]       = useState('');
  const [confirmPwd, setConfirmPwd]   = useState('');

  const [tempToken, setTempToken]         = useState('');
  const [qrCodeDataUri, setQrCodeDataUri] = useState('');
  const [manualEntryKey, setManualEntryKey] = useState('');
  const [code, setCode] = useState('');

  const error = (bootstrap.error || login.error || setup.error || confirm.error || verify.error) as { message?: string } | null;
  const busy  = bootstrap.isPending || login.isPending || setup.isPending || confirm.isPending || verify.isPending;

  // Bootstrap is one-shot. Once an admin exists, /setup has nothing to
  // offer — bounce straight to /login. Spinner shows briefly while the
  // status check resolves so the user never sees a flash of the wizard.
  useEffect(() => {
    if (status.data && !status.data.available) {
      navigate('/login', { replace: true });
    }
  }, [status.data, navigate]);
  if (status.data && !status.data.available) {
    return (
      <AuthShell title="Welcome" subtitle="Redirecting to sign-in…">
        <Stack alignItems="center" sx={{ py: 4 }}>
          <CircularProgress />
        </Stack>
      </AuthShell>
    );
  }

  if (status.isLoading) {
    return (
      <AuthShell title="Welcome" subtitle="Checking platform status…">
        <Stack alignItems="center" sx={{ py: 4 }}>
          <CircularProgress />
        </Stack>
      </AuthShell>
    );
  }

  const passwordOk = password.length >= 12 && password === confirmPwd;

  const onAccountSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!passwordOk) return;
    await bootstrap.mutateAsync({
      username:    username.trim(),                    // preserve case (citext column)
      email:       email.trim().toLowerCase(),
      displayName: displayName.trim(),
      password,
    });
    const loginResult = await login.mutateAsync({
      usernameOrEmail: username.trim(),
      password,
    });
    const setupResult = await setup.mutateAsync(loginResult.tempToken);
    setTempToken(loginResult.tempToken);
    setQrCodeDataUri(setupResult.qrCodeDataUri);
    setManualEntryKey(setupResult.manualEntryKey);
    setStep(1);
  };

  const finishEnrolment = async (otpCode: string) => {
    await confirm.mutateAsync({ tempToken, code: otpCode });
    const loginResult = await login.mutateAsync({
      usernameOrEmail: username,
      password,
    });
    const fullResult = await verify.mutateAsync({
      tempToken: loginResult.tempToken,
      code: otpCode,
    });
    setSessionToken(fullResult.token);
    setStep(2);
    setTimeout(() => navigate('/home'), 1200);
  };

  const onTotpSubmit = async (e: FormEvent) => {
    e.preventDefault();
    await finishEnrolment(code);
  };

  const title    = step === 0 ? 'Welcome to ARMS'
                 : step === 1 ? 'Set up two-factor'
                 : 'You’re in';
  const subtitle = step === 0 ? 'Create the first ACES administrator account.'
                 : step === 1 ? 'Scan the QR code in Microsoft Authenticator (or any TOTP app), then enter the 6-digit code below.'
                 : 'Account created.';

  return (
    <AuthShell
      title={title}
      subtitle={subtitle}
      topSlot={
        <>
          <Stepper activeStep={step} sx={{ mb: 1 }}>
            {STEPS.map((label) => (
              <Step key={label}><StepLabel>{label}</StepLabel></Step>
            ))}
          </Stepper>
          {error?.message && <ErrorAlert>{error.message}</ErrorAlert>}
        </>
      }
    >
      {step === 0 && (
        <form onSubmit={onAccountSubmit}>
          <Stack spacing={2}>
            <TextField
              label="Username"
              autoFocus
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              helperText="What you'll type into the sign-in form. Case-insensitive at login."
              required fullWidth disabled={busy}
            />
            <TextField
              label="Email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required fullWidth disabled={busy}
            />
            <TextField
              label="Display name"
              autoComplete="name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required fullWidth disabled={busy}
            />
            <PasswordField
              label="Password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required fullWidth disabled={busy}
              helperText="At least 12 characters with uppercase, lowercase, digit, and special character."
            />
            <PasswordField
              label="Confirm password"
              autoComplete="new-password"
              value={confirmPwd}
              onChange={(e) => setConfirmPwd(e.target.value)}
              required fullWidth disabled={busy}
              error={confirmPwd.length > 0 && password !== confirmPwd}
              helperText={confirmPwd.length > 0 && password !== confirmPwd
                ? 'Passwords do not match.' : ' '}
            />
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={busy || !username.trim() || !email.trim() || !displayName.trim() || !passwordOk}
              startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={{ py: 1.25, fontWeight: 600, mt: 1 }}
            >
              {busy ? 'Creating account…' : 'Create account'}
            </Button>
          </Stack>
        </form>
      )}

      {step === 1 && (
        <form onSubmit={onTotpSubmit}>
          <Stack spacing={2.5} alignItems="center">
            <Box
              component="img"
              src={qrCodeDataUri}
              alt="TOTP QR code"
              sx={{
                width: 224,
                height: 224,
                p: 1,
                bgcolor: '#fff',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1.5,
                boxShadow: 1,
              }}
            />
            <Box sx={{ textAlign: 'center', width: '100%' }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Manual entry key
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'monospace',
                  bgcolor: 'action.hover',
                  px: 1.5, py: 0.75,
                  borderRadius: 1,
                  display: 'inline-block',
                  mt: 0.5,
                  letterSpacing: 1,
                }}
              >
                {manualEntryKey}
              </Typography>
            </Box>
            <OtpInput
              value={code}
              onChange={setCode}
              onComplete={finishEnrolment}
              disabled={busy}
            />
            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={busy || code.length !== 6}
              startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={{ py: 1.25, fontWeight: 600 }}
            >
              {busy ? 'Enrolling…' : 'Enrol and sign in'}
            </Button>
          </Stack>
        </form>
      )}

      {step === 2 && (
        <Stack spacing={2} alignItems="center" sx={{ py: 2 }}>
          <Alert severity="success" sx={{ width: '100%' }}>
            Account created. Redirecting to your home dashboard…
          </Alert>
          <CircularProgress size={28} />
        </Stack>
      )}
    </AuthShell>
  );
}
